import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createEntityAdapter } from '@reduxjs/toolkit'
import { useWatch, useFormContext } from 'react-hook-form'
import { getConfigs } from '../../../../redux/slices'
import { AnalyzerDropdown } from '../components/AnalyzerDropdown'
import { DefaultState } from '../../../../types'

interface ConfigMinimal {
  id: string | number
  name: string
}

const ConfigSelectorWrapper = () => {
  const dispatch = useDispatch()
  const { control, getValues, setValue } = useFormContext()
  const scriptId = useWatch({ control, name: 'scriptId' })
  const configId = useWatch({ control, name: 'configId' })

  const lastConfigsRef = useRef<Record<string, string | undefined>>({})
  const initialScriptRef = useRef<string | number | undefined>(scriptId)

  // Get configs from Redux
  const configs = createEntityAdapter<ConfigMinimal>()
    .getSelectors()
    .selectAll(useSelector((state: DefaultState) => state.configs))

  const options = configs.map((c) => ({
    label: c.name,
    value: c.id
  }))

  useEffect(() => {
    if (!scriptId) return
    dispatch(getConfigs(scriptId))

    // Track script change and update configId accordingly
    const prevScript = initialScriptRef.current

    if (prevScript !== scriptId) {
      // Save last known configId for previous script
      if (prevScript) {
        lastConfigsRef.current[prevScript] = getValues('configId')
      }

      // Restore previous configId for current script (if any)
      const previousConfig = lastConfigsRef.current[scriptId]

      setValue('configId', previousConfig ?? '', {
        shouldDirty: true,
        shouldValidate: false
      })

      initialScriptRef.current = scriptId
    }
  }, [scriptId, dispatch, getValues, setValue])

  return (
    <AnalyzerDropdown
      name="configId"
      label="Configuration"
      options={options}
      isRequired={true}
    />
  )
}

export default ConfigSelectorWrapper
